import {
    FlowTaskMessage,
    getFirebase,
    resolveAttachedFileFingerprint
} from '@assets/firebase/FirebaseAdapater';
import {
    TaskDefinition,
    useStore,
    useStoreUI,
    useTasksStore
} from '@assets/model/Store';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { GetIcon } from '../../Icons/Icons';
import { Panel } from '../../Panel/Panel';
import { Flex } from '../../UI/Flex/Flex';
import { TaskChatContainer } from '../TaskChatContainer/TaskChatContainer';
import * as style from './TaskChatPanel.module.less';
import { onValue, ref } from 'firebase/database';
interface TaskChatPanelProps {
    taskId: string;
    isLocked: boolean;
    isVarSaved: boolean;
}

export const TaskChatPanel = observer((p: TaskChatPanelProps) => {
    const store = useStore();

    const { taskId, isLocked } = p;
    const reportId = store.selectedReportId;
    const task: TaskDefinition = useTasksStore().get_task_definition(taskId);
    const [inputText, setInputValue] = useState('');
    const [attachmentNextMessage, setAttachmentNextMessage] = useState<
        string[]
    >([]);

    const ui = useStoreUI();
    const refInput = useRef<HTMLTextAreaElement>(null);
    //Both panel and container use this, makes sense to move it into a store\something
    const [chatContent, setChatContent] = useState(null);
    // When the approval card is showing, the composer is locked. Clicking
    // "I have feedback" flips this on: the card hides, the composer unlocks and
    // takes focus so the user can type their feedback into the normal chat.
    const [feedbackMode, setFeedbackMode] = useState(false);

    // A new message means a fresh approval cycle — bring the card back and
    // re-lock the composer.
    const chatLength = chatContent?.length ?? 0;
    useEffect(() => {
        setFeedbackMode(false);
    }, [chatLength, taskId]);

    useEffect(
        function fetchTaskContent() {
            const path = `/flowtasks/history/${reportId}/${taskId}/results`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const data = snapshot.val();
                console.log('TaskChatPanel: fetched task chat data:', data);
                setChatContent(data);
            });

            return () => {
                unsub();
            };
        },
        [taskId, reportId]
    );

    const onClickSend = () => {
        if (ui.chatWaitingForResponse) return;
        if (composerLocked) return;

        var input = refInput.current.value;
        if (input.length == 0) {
            input = ' ';
        }

        const taskStateForCheck =
            store.flowTaskState?.[store.selectedReportId]?.[taskId];
        const isModuleComplete =
            taskStateForCheck?.state_vars?.IS_COMPLETE === '1';
        const hasAttachments = attachmentNextMessage.length > 0;

        // Text-only message on a completed module: route through chat_refine
        // so the flow edits a staging copy of the saved output instead of
        // re-running the whole pipeline from scratch. The user can keep
        // iterating via chat indefinitely; the canonical only changes when
        // they explicitly Accept the staging proposal in the bubble.
        if (
            isModuleComplete &&
            !isLastMessageHumanInTheLoop &&
            !hasAttachments
        ) {
            const fb = getFirebase();
            fb.flowtask_invoke_chat_refine(
                store.selectedReportId,
                taskId,
                input
            );
            setAttachmentNextMessage([]);
            setInputValue('');
            return;
        }

        console.log(
            'Sending message to flow_id:',
            task.flow_id,
            'message:',
            input
        );
        const fb = getFirebase();
        const reportId = store.selectedReportId;
        var messageToSend: FlowTaskMessage = {
            content: input
        };

        if (attachmentNextMessage.length > 0) {
            messageToSend.attachedFiles = attachmentNextMessage;
            // Resolve a content fingerprint per file so the comparator detector
            // can tell apart "same content re-attached" from "new content".
            // Falls back to filename when ragDocId isn't available yet.
            messageToSend.attachedFilesFingerprints = attachmentNextMessage.map(
                path =>
                    resolveAttachedFileFingerprint(
                        store.selectedReportId,
                        path,
                        store.allProjectFiles
                    )
            );
        }

        //Approve/reject messages (direct logic in flowise)
        //Need approve yes\no -> No is normal chat if its a human-in-loop
        //Next step is done with ChatMessageApproveReject component

        if (isLastMessageHumanInTheLoop) {
            messageToSend.approve = false;
        }
        fb.flowtask_add_prompt_job(reportId, taskId, messageToSend);
        setAttachmentNextMessage([]);
        setInputValue('');
    };

    const waitingForReply = (
        <i>
            <div
                className="spinner-border spinner-border-sm"
                role="status"
                style={{
                    animationDuration: '1.25s',
                    width: '20px',
                    height: '20px'
                }}
            ></div>{' '}
        </i>
    );

    var isLastMessageHumanInTheLoop = false;
    if (chatContent != null && chatContent.length > 0) {
        const lastMessage = chatContent[chatContent.length - 1] || null;

        if (lastMessage?.action != null) {
            isLastMessageHumanInTheLoop = true;
        }
    }

    // The approval card is showing whenever the last message needs a human
    // decision and the user hasn't chosen to give feedback yet.
    const awaitingApproval = isLastMessageHumanInTheLoop && !feedbackMode;
    // Composer is locked while awaiting approval (or when the panel is locked).
    let composerLocked = isLocked || awaitingApproval;
    if (p.isVarSaved) {
        composerLocked = false;
    }

    const onGiveFeedback = () => {
        setFeedbackMode(true);
        setTimeout(() => refInput.current?.focus(), 0);
    };

    const taskState = store.flowTaskState?.[reportId]?.[taskId];
    const isWaitingReply =
        taskState != null &&
        taskState?.status != 'completed' &&
        taskState?.status != 'failed';

    const isLockedStyle = composerLocked ? style.lockedInput : '';

    const sendButton = (
        <div
            className={`${style.sendButton} ${isLockedStyle}`}
            onClick={() => onClickSend()}
        >
            {isWaitingReply ? (
                waitingForReply
            ) : (
                <img src={GetIcon('send-03')} className={style.sendIcon} />
            )}
        </div>
    );

    // Clears the composer; only shown once there is text to clear.
    const clearButton = inputText.length > 0 && (
        <div
            className={`${style.clearInputButton} ${isLockedStyle}`}
            onClick={() => setInputValue('')}
        >
            <img src={GetIcon('x-close')} className={style.clearIcon} />
        </div>
    );

    return (
        <Panel fillCell={true} className={style.customShadow}>
            <Flex direction="column" fillCell={true}>
                <div className={style.messages}>
                    <TaskChatContainer
                        showWaitingAnimation={isWaitingReply}
                        isWaitingReply={isWaitingReply}
                        reportId={store.selectedReportId}
                        taskId={taskId}
                        hideApprovalCard={feedbackMode}
                        isVarSaved={p.isVarSaved}
                        onFeedback={onGiveFeedback}
                    />
                </div>
                <div className={style.inputLineOffset}>
                    <div className={`${style.inputBox} ${isLockedStyle}`}>
                        {/* Grid auto-grow: the ::after mirror replicates the
                            text and sets the height, so the textarea grows with
                            each wrapped line — no JS, no scrollHeight. */}
                        <div
                            className={style.input}
                            data-replicated-value={inputText}
                        >
                            <textarea
                                ref={refInput}
                                rows={1}
                                value={inputText}
                                placeholder={
                                    feedbackMode
                                        ? 'Give feedback or ask a question'
                                        : 'Enter a question'
                                }
                                autoFocus={true}
                                disabled={composerLocked}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        onClickSend();
                                    }
                                }}
                                onChange={e => {
                                    setInputValue(e.currentTarget.value);
                                }}
                            ></textarea>
                        </div>
                        {clearButton}
                        {sendButton}
                    </div>
                </div>
            </Flex>
        </Panel>
    );
});
